// Central state of the Factures page: all data loading, mutations and
// form state live in ONE hook (useFacturesState) shared via a context so
// the leaf components (mensualitats grid, cards, drawer, moviments panel,
// entry rows, forms) consume it without prop-drilling.

import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createContext } from 'react';

import { axiosInstance as axios } from '../../config/axios';
import { useAuth } from '../../hooks/useAuth';
import {
	FINANCES_MONEY_ROLES,
	FACTURES_UNITATS,
} from '../../constants/roles';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	addMonthsYm,
	currentYm,
	currentSchoolYear,
	moneyInputToCents,
	MENSUAL_WINDOW,
	type IEntry,
	type ISummaryRow,
	type IUnitatSummary,
	type MensualQuickAdd,
} from './facturesTypes';

interface IEditState {
	id: string;
	amount: string;
	concept: string;
	capName: string;
	unitatCharge: string;
	reimbursed: string;
	expenseDate: string;
}

// Internal to this module: the generic state for any factures user.
const useFacturesState = (initialYear?: string) => {
	const { user } = useAuth();
	const roles = (user?.roles as readonly string[]) ?? [];
	const money =
		FINANCES_MONEY_ROLES.some((r) => roles.includes(r)) || false;

	// unitat-role users are scoped to their own unitat (server-enforced);
	// the year summary response carries the abbreviation in ownUnitat
	const [ownUnitat, setOwnUnitat] = useState<string | null>(null);
	const canSeeGlobal =
		money || roles.includes('Caps') || roles.includes('Agrupament');

	const [year, setYear] = useState<string>(
		initialYear || currentSchoolYear()
	);
	const [selected, setSelected] = useState<string | null>(null);
	const [summary, setSummary] = useState<Record<
		string,
		ISummaryRow
	> | null>(null);
	const [detail, setDetail] = useState<IUnitatSummary | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [capNames, setCapNames] = useState<string[]>([]);

	// despesa form state
	const [showForm, setShowForm] = useState(false);
	const [fAmount, setFAmount] = useState('');
	const [fConcept, setFConcept] = useState('');
	const [fCapName, setFCapName] = useState('');
	const [fDate, setFDate] = useState('');
	const [fReceipt, setFReceipt] = useState<File | null>(null);
	const [fUnitatCharge, setFUnitatCharge] = useState('');
	const [submitting, setSubmitting] = useState(false);

	// tresoreria forms + inline quick-add popover of the mensualitats grid
	const [quickAdd, setQuickAdd] = useState<MensualQuickAdd | null>(null);
	const [quickFirstMonth, setQuickFirstMonth] = useState<string>(currentYm());
	const [quickBusy, setQuickBusy] = useState(false);
	const [showAjust, setShowAjust] = useState(false);
	const [aAmount, setAAmount] = useState<string>('');
	const [aNote, setANote] = useState('');

	const [edit, setEdit] = useState<IEditState | null>(null);
	const [editReceipt, setEditReceipt] = useState<File | null>(null);
	const [capFilter, setCapFilter] = useState<string>('tots');
	const [globalSummary, setGlobalSummary] = useState<{
		totalOwedCents: number;
		byCap: { capName: string; owedCents: number }[];
	} | null>(null);

	const [moviments, setMoviments] = useState<IEntry[] | null>(null);
	const [movUnitat, setMovUnitat] = useState<string>('tots');
	const [movEstat, setMovEstat] = useState<
		'tots' | 'pendents' | 'reemborsats'
	>('tots');
	const [movCap, setMovCap] = useState<string>('tots');
	const [zipBusy, setZipBusy] = useState(false);

	// --- Session year-caches ---
	// Year summaries / movements / unitat details are loaded fresh on first
	// view and then written THROUGH to per-year caches. Switching back to a
	// previously-viewed yearhydrates instantly from the cache, then a
	// background refetch refreshes the values in place (so real changes made
	// by other users still appear). All mutations write-through on the same
	// load helpers, so caches never go divergent within a session.
	// (the year-summary response carries the extra ownUnitat field on top of
	// the per-unitat row map, so the cache type mirrors the real shape)
	const summaryCacheRef = useRef(
		new Map<string, Record<string, ISummaryRow> & { ownUnitat?: string | null }>()
	);
	const movimentsCacheRef = useRef(new Map<string, IEntry[]>());
	const detailCacheRef = useRef(new Map<string, IUnitatSummary>());

	const loadMoviments = useCallback(async (y: string) => {
		const cached = movimentsCacheRef.current.get(y);
		if (cached) setMoviments(cached); // instant paint for a revisited year
		try {
			const res = await axios.get(`/api/finances/moviments/${y}`);
			movimentsCacheRef.current.set(y, res.data);
			setMoviments(res.data);
		} catch {
			movimentsCacheRef.current.delete(y);
			if (!cached) setMoviments([]); // keep cached data on refetch failure
		}
	}, []);

	const loadSummary = useCallback(
		async (y: string) => {
			const cached = summaryCacheRef.current.get(y);
			if (cached) {
				setSummary(cached);
				setOwnUnitat(cached.ownUnitat ?? null);
			}
			setError(null);
			try {
				const res = await axios.get(`/api/finances/${y}`);
				summaryCacheRef.current.set(y, res.data);
				setSummary(res.data);
				setOwnUnitat(res.data.ownUnitat ?? null);
				if (money) void loadMoviments(y);
			} catch (e) {
				if (!cached) {
					setError(extractErrorMessage(e as Error));
				}
				// cache present: keep showing the cached year quietly, refetch next visit
				if (money && !cached) void loadMoviments(y);
			}
		},
		[money, loadMoviments]
	);

	// Stale-response guard: track the (unitat, year) we last requested so
	// a slow older response can never overwrite the newer unitat's data
	// (detail used to show stale saldo/entries during switching).
	const detailReqRef = useRef('');
	const loadDetail = useCallback(async (y: string, unitat: string) => {
		if (!unitat) return;
		setError(null);
		const req = `${y}/${unitat}`;
		detailReqRef.current = req;
		const cached = detailCacheRef.current.get(req);
		if (cached) {
			// instant paint for a revisited unitat; background refetch below
			setDetail(cached);
		} else {
			setDetail(null); // no stale unitat data while the request runs
		}
		try {
			const res = await axios.get(`/api/finances/${y}/${unitat}`);
			if (detailReqRef.current !== req) return; // superseded
			detailCacheRef.current.set(req, res.data);
			setDetail(res.data);
		} catch (e) {
			if (detailReqRef.current !== req || cached) return;
			setError(extractErrorMessage(e as Error));
		}
	}, []);

	const loadGlobal = useCallback(async () => {
		try {
			const res = await axios.get('/api/finances/global-summary');
			setGlobalSummary(res.data);
		} catch {
			/* banner is best-effort */
		}
	}, []);

	const loadCapNames = useCallback(async () => {
		try {
			const res = await axios.get('/api/finances/cap-names');
			setCapNames(res.data.map((r: { capName: string }) => r.capName));
		} catch {
			/* autocomplete is best-effort */
		}
	}, []);

	useEffect(() => {
		loadSummary(year);
	}, [year, loadSummary]);

	useEffect(() => {
		if (money) loadMoviments(year);
	}, [year, money, loadMoviments]);

	useEffect(() => {
		loadGlobal();
		loadCapNames();
	}, [loadGlobal, loadCapNames, detail?.entries.length]);

	useEffect(() => {
		if (selected) loadDetail(year, selected);
	}, [selected, year, loadDetail]);

	const openUnitat = (unitat: string) => {
		setShowForm(false);
		setEdit(null);
		if (selected === unitat) {
			// clicking the selected unitat again = deselect
			setSelected(null);
			if (money) setMovUnitat('tots');
			return;
		}
		setSelected(unitat);
		// selecting a unitat FILTERS the global movements panel
		// (no separate per-unitat list for money users)
		if (money) setMovUnitat(unitat);
	};

	// unitat accounts land directly inside their unitat's view
	useEffect(() => {
		if (ownUnitat && !selected) openUnitat(ownUnitat);
	}, [ownUnitat, selected, openUnitat]);

	const addDespesa = async () => {
		const cents = moneyInputToCents(fAmount);
		if (cents == null || cents <= 0) {
			setError('Import no vàlid');
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			const fd = new FormData();
			fd.set('unitat', selected!);
			fd.set('schoolYear', year);
			fd.set('amountCents', String(cents));
			fd.set('concept', fConcept);
			fd.set('capName', fCapName);
			if (fDate) fd.set('expenseDate', new Date(fDate).toISOString());
			if (fUnitatCharge) {
				const uc = moneyInputToCents(fUnitatCharge);
				if (uc == null || uc < 0 || uc > cents) {
					setError('La part de la unitat no és vàlida');
					setSubmitting(false);
					return;
				}
				fd.set('unitatChargeCents', String(uc));
			}
			if (fReceipt) fd.set('receipt', fReceipt);
			await axios.post('/api/finances/despesa', fd);
			setFConcept('');
			setFAmount('');
			setFCapName('');
			setFDate('');
			setFReceipt(null);
			setFUnitatCharge('');
			setShowForm(false);
			loadDetail(year, selected!);
			loadSummary(year);
			loadCapNames();
		} catch (e) {
			setError(extractErrorMessage(e as Error));
		}
		setSubmitting(false);
	};

	const doReimburse = async (id: string) => {
		if (
			!window.confirm(
				"Confirmes el reemborsament complet de la despesa? (s'avançaran els apunts del llibre)"
			)
		)
			return;
		setError(null);
		try {
			await axios.post(`/api/finances/despesa/${id}/reimburse`);
			loadDetail(year, selected!);
			loadSummary(year);
		} catch (e) {
			setError(extractErrorMessage(e as Error));
		}
	};

	const doDespesaDelete = async (id: string) => {
		if (!window.confirm('Esborrar aquesta despesa?')) return;
		setError(null);
		try {
			await axios.delete(`/api/finances/despesa/${id}`);
			loadDetail(year, selected!);
			loadSummary(year);
		} catch (e) {
			setError(extractErrorMessage(e as Error));
		}
	};

	const doEntryDelete = async (id: string) => {
		if (!window.confirm('Esborrar aquest apunt?')) return;
		setError(null);
		try {
			await axios.delete(`/api/finances/${id}`);
			loadDetail(year, selected!);
			loadSummary(year);
		} catch (e) {
			setError(extractErrorMessage(e as Error));
		}
	};

	const startEdit = (e: IEntry) => {
		setEdit({
			id: e._id,
			amount: (e.amountCents / 100).toFixed(2).replace('.', ','),
			concept: e.concept ?? '',
			capName: e.capName ?? '',
			unitatCharge:
				e.unitatChargeCents != null
					? (e.unitatChargeCents / 100).toFixed(2).replace('.', ',')
					: '',
			reimbursed:
				e.reimbursedAmountCents != null
					? (e.reimbursedAmountCents / 100).toFixed(2).replace('.', ',')
					: '',
			expenseDate: e.expenseDate
				? new Date(e.expenseDate).toISOString().slice(0, 10)
				: '',
		});
		setEditReceipt(null);
	};

	const saveEdit = async () => {
		if (!edit || !detail) return;
		const body: Record<string, unknown> = {};
		const cents = moneyInputToCents(edit.amount);
		if (cents == null) {
			setError('Import no vàlid');
			return;
		}
		body.amountCents = cents;
		if (edit.capName) body.capName = edit.capName;
		if (edit.concept) body.concept = edit.concept;
		if (edit.expenseDate) {
			body.expenseDate = new Date(edit.expenseDate).toISOString();
		}
		if (money && editReceipt) body.receipt = editReceipt; // handled via FormData below
		// Only money roles may change the split — backend 403s it otherwise,
		// and the field is hidden for non-money users anyway (their prefilled
		// value used to be sent regardless, 403ing every edit).
		if (money && edit.unitatCharge !== '') {
			const uc = moneyInputToCents(edit.unitatCharge);
			if (uc == null || uc < 0) {
				setError('Part de la unitat no vàlida');
				return;
			}
			body.unitatChargeCents = uc;
		}
		setSubmitting(true);
		setError(null);
		try {
			const hasFile = money && editReceipt;
			const entry = detail.entries.find((x) => x._id === edit.id);
			const isReimbursedEntry = entry?.reimbursedAmountCents != null;
			if (isReimbursedEntry && money && edit.reimbursed === '') {
				body.reimbursedAmountCents = 0;
			} else if (isReimbursedEntry && money && edit.reimbursed !== '') {
				const r = moneyInputToCents(edit.reimbursed);
				if (r == null) {
					setError('Reemborsament no vàlid');
					setSubmitting(false);
					return;
				}
				body.reimbursedAmountCents = r;
			}
			let res;
			if (hasFile) {
				const fd = new FormData();
				Object.entries(body).forEach(([k, v]) => fd.set(k, String(v)));
				fd.set('receipt', editReceipt!);
				res = await axios.patch(
					`/api/finances/despesa/${edit.id}`,
					fd
				);
			} else {
				res = await axios.patch(`/api/finances/despesa/${edit.id}`, body);
			}
			setEdit(null);
			setEditReceipt(null);
			loadDetail(year, selected!);
			loadSummary(year);
			res; // void
		} catch (e) {
			setError(extractErrorMessage(e as Error));
		}
		setSubmitting(false);
	};

	// “＋” chip in the mensualitats overview → inline popover under that
	// unitat's row, amount prefilled from its latest earlier mensual.
	const quickMensual = (unitat: string, month: string) => {
		const ms = summary?.[unitat]?.mensuals ?? {};
		const prior = Object.keys(ms).filter((m) => m < month).sort();
		const amtCents = prior.length ? ms[prior[prior.length - 1]] : null;
		setQuickAdd({
			unitat,
			month,
			amount:
				amtCents != null
					? (amtCents / 100).toFixed(2).replace('.', ',')
					: '',
		});
	};

	// First mensual of the year: the popover offers a month selector so the
	// treasurer picks the starting month freely (8-month window from there).
	const quickMensualStart = (unitat: string) => {
		setQuickFirstMonth(currentYm());
		setQuickAdd({ unitat, month: null, amount: '' });
	};

	// Panel entry point: month as a string opens a prefilled popover for an
	// existing window; null opens the first-mensural month picker.
	const quickMensualOpen = (unitat: string, month: string | null) => {
		if (month === null) quickMensualStart(unitat);
		else quickMensual(unitat, month);
	};

	const closeQuickAdd = () => setQuickAdd(null);

	// Missing (non-future) mensualitats across units — powers the warning
	// badge in the top row of the overview panel.
	const mensualMissing = useMemo(() => {
		if (!money || ownUnitat) return [];
		const cur = currentYm();
		const out: { unitat: string; nom: string; month: string }[] = [];
		for (const u of FACTURES_UNITATS) {
			const ms = summary?.[u.id]?.mensuals ?? {};
			const monthsIn = Object.keys(ms).sort();
			if (monthsIn.length === 0) {
				// window never started: the current month counts as pending
				// (the row's own ＋ Primer mes opens the same flow)
				out.push({ unitat: u.id, nom: u.nom, month: cur });
				continue;
			}
			const start = monthsIn[0];
			for (let i = 0; i < MENSUAL_WINDOW; i++) {
				const ym = addMonthsYm(start, i);
				if (ym > cur) break; // future months can't be missing yet
				if (ms[ym] === undefined) {
					out.push({ unitat: u.id, nom: u.nom, month: ym });
				}
			}
		}
		return out;
	}, [money, ownUnitat, summary, year]);

	const registerMensual = async (skip = false) => {
		if (!quickAdd || quickBusy) return;
		const { unitat } = quickAdd;
		const month = quickAdd.month ?? quickFirstMonth;
		if (!month) return;
		const cents = skip ? 0 : moneyInputToCents(quickAdd.amount);
		if (cents == null) {
			setError('Import no vàlid');
			return;
		}
		setQuickBusy(true);
		setError(null);
		try {
			await axios.post('/api/finances/mensual', {
				unitat,
				schoolYear: year,
				month,
				amountCents: cents,
				note: cents === 0 ? 'Mensualitat saltada' : undefined,
			});
			setQuickAdd(null);
			loadSummary(year);
			if (selected === unitat) loadDetail(year, unitat);
		} catch (e) {
			setError(extractErrorMessage(e as Error));
		}
		setQuickBusy(false);
	};

	const addAjust = async () => {
		const cents = moneyInputToCents(aAmount);
		if (cents == null) {
			setError('Import no vàlid');
			return;
		}
		if (!aNote.trim()) {
			setError('Cal indicar el motiu de l\u2019ajust');
			return;
		}
		setError(null);
		try {
			await axios.post('/api/finances/ajust', {
				unitat: selected!,
				schoolYear: year,
				amountCents: cents,
				note: aNote,
			});
			setANote('');
			setAAmount('');
			setShowAjust(false);
			loadDetail(year, selected!);
			loadSummary(year);
		} catch (e) {
			setError(extractErrorMessage(e as Error));
		}
	};

	const canWriteEntry = (e: IEntry): boolean => {
		if (money) return true;
		if (e.reimbursedAmountCents != null) return false;
		if (user?.username && e.createdBy === user.username) return true;
		// JWT's unitat is an ObjectId — compare against the abbreviation
		// resolved server-side (year summary's ownUnitat) instead.
		if (roles.includes('Unitat') && ownUnitat && e.unitat === ownUnitat)
			return true;
		return false;
	};

	return {
		// identity
		roles,
		money,
		ownUnitat,
		canSeeGlobal,
		// data
		year,
		setYear,
		selected,
		summary,
		detail,
		moviments,
		movUnitat,
		movEstat,
		capFilter,
		setCapFilter,
		movCap,
		setMovUnitat,
		setMovEstat,
		setMovCap,
		zipBusy,
		setZipBusy,
		capNames,
		globalSummary,
		error,
		setError,
		mensualMissing,
		// despesa form
		showForm,
		setShowForm,
		fAmount,
		setFAmount,
		fConcept,
		setFConcept,
		fCapName,
		setFCapName,
		fDate,
		setFDate,
		fReceipt,
		setFReceipt,
		fUnitatCharge,
		setFUnitatCharge,
		submitting,
		addDespesa,
		// ajust form
		showAjust,
		setShowAjust,
		aAmount,
		setAAmount,
		aNote,
		setANote,
		addAjust,
		// edit flow
		edit,
		setEdit,
		editReceipt,
		setEditReceipt,
		startEdit,
		saveEdit,
		// actions
		openUnitat,
		doReimburse,
		doDespesaDelete,
		doEntryDelete,
		registerMensual,
		quickAdd,
		setQuickAdd,
		quickFirstMonth,
		setQuickFirstMonth,
		quickBusy,
		quickMensualOpen,
		quickMensual,
		closeQuickAdd,
		canWriteEntry,
	} as const;
};

export type FacturesState = ReturnType<typeof useFacturesState>;

const FacturesContext = createContext<FacturesState | null>(null);

export function FacturesProvider({
	children,
	initialYear,
}: {
	children: React.ReactNode;
	initialYear?: string;
}) {
	const state = useFacturesState(initialYear);
	return (
		<FacturesContext.Provider value={state}>
			{children}
		</FacturesContext.Provider>
	);
}

export function useFactures(): FacturesState {
	const ctx = useContext(FacturesContext);
	if (!ctx) throw new Error('useFactures outside FacturesProvider');
	return ctx;
}

// Exported so other modules can type-check against pieces of the state.
export type { IEditState };
